import React, { useEffect, useState } from "react"
import { Grid } from "@material-ui/core"
import clsx from "clsx"
import { useHistory } from "react-router-dom"
import { AttendanceRoutes } from "@/routes/private"

import InboxSidebar, { InboxInnerType } from "@/pages/Attendance/Sidebar"

import ProfileInfo from "@/pages/Attendance/Chat/ChatListPanel/ChatListHeader/ProfileInfo"
import UserSettings from "@/pages/Attendance/Chat/ChatListPanel/ChatListHeader/UserSettings"
import SecuritySettings from "@/pages/Attendance/Chat/ChatListPanel/ChatListHeader/SecuritySettings"

import { MobileNavProvider } from "@/contexts/MobileNavContext"

import MobileBottomTabBar from "@/pages/Attendance/MobileBottomTabBar"

import useStyles from "@/pages/Attendance/styles"
import useDidMount from "@/hooks/useDidMount"
import useWABACredits from "@/@integrations/WABA/hooks/useWABACredits"
import AnalyticsService from "@/services/Analytics"
import { UpsellDialog } from "@/components"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"
import HardCoded from "@/services/HardCoded"
import StorageService from "@/services/Storage"
import { runAfterReactRender } from "@/utils/node"
import { isSmallScreen } from "@/utils/checkDevice"

import { useGlobalStateStore } from "@/store/GlobalState"
import { hubAppUrl } from "@/config/url"
import { BOARD_NAVIGATE_TO_CONTACT_MESSAGE_TYPE, INBOX_OPEN_CHAT_BY_PHONE_EVENT } from "@/pages/Attendance/boardNavigationConstants"

const BOARD_EMBED_QUERY_PARAM = "embed=board"

const LECHAT_IFRAME_URL = "https://pixel-perfect-snapshot-7907.lovable.app/chats"

const Attendance: React.FC = () => {
	const classes = useStyles()
	const history = useHistory()

	const { isSidebarCollapsed, setIsSidebarCollapsed } = useGlobalStateStore()

	const [inboxInner, setInboxInner] = useState<InboxInnerType>("inbox")
	const [hasBoardBeenOpened, setHasBoardBeenOpened] = useState<boolean>(false)
	const [hasLeChatBeenOpened, setHasLeChatBeenOpened] = useState<boolean>(false)
	const [profileInfoOpen, setProfileInfoOpen] = useState<boolean>(false)
	const [userSettingsOpen, setUserSettingsOpen] = useState<boolean>(false)
	const [securitySettingsOpen, setSecuritySettingsOpen] = useState<boolean>(false)
	const [isMoreSheetOpen, setIsMoreSheetOpen] = useState<boolean>(false)

	const wabaTemplateCreditsQuantityLimit = useSubscriptionLimits("quantity", "waba_template_credits")
	const canUseWABATemplateCreditsLimit = HardCoded.checkFeatureFlag("canUseWABATemplateCreditsLimit")

	const {
		handleOpenTemplateSendLimitNearLimitDialog,
		isWABATemplateCreditsLoading
	} = useWABACredits()

	useDidMount(() => {
		window.name = "inbox_attendance_page"

		window.onunload = () => {
			window.name = ""
		}

		AnalyticsService.capture("inbox_opened")
	})

	useEffect(() => {
		if (!isWABATemplateCreditsLoading && canUseWABATemplateCreditsLimit) {
			if (wabaTemplateCreditsQuantityLimit.mustBlock) {
				runAfterReactRender(() => UpsellDialog.open({
					dialogCode: wabaTemplateCreditsQuantityLimit.blockCode
				}))
			} else {
				runAfterReactRender(() => handleOpenTemplateSendLimitNearLimitDialog())
			}
		}
	}, [wabaTemplateCreditsQuantityLimit.mustBlock, isWABATemplateCreditsLoading])

	useEffect(() => {
		if (!hubAppUrl.cpURL) {
			return
		}

		const expectedOrigin = new URL(hubAppUrl.cpURL).origin

		const handleBoardNavigateToContact = (event: MessageEvent) => {
			const isFromExpectedOrigin = event.origin === expectedOrigin
			const isBoardNavigateToContactMessage = event.data?.type === BOARD_NAVIGATE_TO_CONTACT_MESSAGE_TYPE

			if (!isFromExpectedOrigin || !isBoardNavigateToContactMessage) {
				return
			}

			const phone = event.data?.phone
			const clientId = event.data?.clientId
			const isValidPhone = typeof phone === "string" && phone.length > 0
			const isValidClientId = (typeof clientId === "number" || typeof clientId === "string") && Number.isInteger(Number(clientId)) && Number(clientId) > 0

			if (!isValidPhone && !isValidClientId) {
				return
			}

			setInboxInner("inbox")

			window.dispatchEvent(new CustomEvent(INBOX_OPEN_CHAT_BY_PHONE_EVENT, {
				detail: {
					phone: isValidPhone ? phone : undefined,
					clientId: isValidClientId ? clientId : undefined
				}
			}))
		}

		window.addEventListener("message", handleBoardNavigateToContact)

		return () => {
			window.removeEventListener("message", handleBoardNavigateToContact)
		}
	}, [])

	const handleInboxClick = () => {
		setInboxInner("inbox")
	}

	const handleAdminClick = () => {
		history.push("/admin/dashboard")
	}

	const handleToggleMoreSheet = () => {
		setIsMoreSheetOpen(previousValue => !previousValue)
	}

	const handleCloseMoreSheet = () => {
		setIsMoreSheetOpen(false)
	}

	const handleBoardClick = () => {
		setInboxInner("board")
		setHasBoardBeenOpened(true)
	}

	const handleLeChatClick = () => {
		setInboxInner("lechat")
		setHasLeChatBeenOpened(true)
	}

	const isBoardInner = inboxInner === "board"
	const isLeChatInner = inboxInner === "lechat"

	const boardIframeSrc = `${hubAppUrl.cpURL}/?${BOARD_EMBED_QUERY_PARAM}`

	const handleToggleSidebarCollapse = () => {
		const nextValue = !isSidebarCollapsed

		setIsSidebarCollapsed(nextValue)
		StorageService.set(StorageService.reservedKeys.INBOX_SIDEBAR_COLLAPSED, nextValue)
	}

	const handleProfileInfoOpen = () => {
		setProfileInfoOpen(true)
	}

	const handleProfileInfoClose = () => {
		setProfileInfoOpen(false)
	}

	const handleUserSettingsOpen = () => {
		setUserSettingsOpen(true)
	}

	const handleUserSettingsClose = () => {
		setUserSettingsOpen(false)
	}

	const handleSecuritySettingsOpen = () => {
		setSecuritySettingsOpen(true)
	}

	const handleSecuritySettingsClose = () => {
		setSecuritySettingsOpen(false)
	}

	const sidebarGridClassName = isSidebarCollapsed
		? classes.sidebarGridCollapsed
		: classes.sidebarGridExpanded

	return (
		<MobileNavProvider>
			<Grid
				container
				direction="column"
				wrap="nowrap"
			>
				<Grid
					container
					className={classes.attendanceContainer}
				>
					{!isSmallScreen && (
						<Grid
							item
							className={sidebarGridClassName}
						>
							<InboxSidebar
								inboxInner={inboxInner}
								onInboxClick={handleInboxClick}
								onBoardClick={handleBoardClick}
								onLeChatClick={handleLeChatClick}
								isCollapsed={isSidebarCollapsed}
								onToggleCollapse={handleToggleSidebarCollapse}
								onProfileInfoOpen={handleProfileInfoOpen}
								onUserSettingsOpen={handleUserSettingsOpen}
								onSecuritySettingsOpen={handleSecuritySettingsOpen}
								isUserSettingsOpen={userSettingsOpen}
							/>
						</Grid>
					)}

					{hasBoardBeenOpened && (
						<Grid
							item
							xs
							className={clsx(classes.boardIframeContainer, !isBoardInner && classes.hiddenView)}
						>
							<iframe
								src={boardIframeSrc}
								title="Funil"
								className={classes.boardIframe}
							/>
						</Grid>
					)}

					{hasLeChatBeenOpened && (
						<Grid
							item
							xs
							className={clsx(classes.boardIframeContainer, !isLeChatInner && classes.hiddenView)}
						>
							<iframe
								src={LECHAT_IFRAME_URL}
								title="LeChat"
								className={classes.boardIframe}
							/>
						</Grid>
					)}
					<Grid
						container
						justify="center"
						className={clsx(classes.routesContainer, (isBoardInner || isLeChatInner) && classes.hiddenView)}
					>
						<Grid
							container
							className={classes.routesContent}
						>
							<AttendanceRoutes />
						</Grid>
					</Grid>
				</Grid>

				<ProfileInfo
					openDrawer={profileInfoOpen}
					onClose={handleProfileInfoClose}
					isCollapsed={isSidebarCollapsed}
				/>

				<UserSettings
					openDrawer={userSettingsOpen}
					onClose={handleUserSettingsClose}
					isCollapsed={isSidebarCollapsed}
				/>

				<SecuritySettings
					openDrawer={securitySettingsOpen}
					onClose={handleSecuritySettingsClose}
					isCollapsed={isSidebarCollapsed}
				/>

			</Grid>
			{isSmallScreen && (
				<MobileBottomTabBar
					activeSection={inboxInner}
					isMoreSheetOpen={isMoreSheetOpen}
					isUserSettingsOpen={userSettingsOpen}
					onInboxClick={handleInboxClick}
					onAdminClick={handleAdminClick}
					onMoreClick={handleToggleMoreSheet}
					onCloseMoreSheet={handleCloseMoreSheet}
					onUserSettingsOpen={handleUserSettingsOpen}
				/>
			)}
		</MobileNavProvider>
	)
}

export default Attendance
